/** Format a decimal fraction as a signed percentage. 0.31 → "+31.00%", -0.05 → "-5.00%" */
export function formatPct(v: number): string {
  return `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`
}

/** Format a decimal fraction as percentage. 0.31 → "31.00%", decimals configurable. */
export function formatPercent(v: number, decimals = 2): string {
  return `${(v * 100).toFixed(decimals)}%`
}

/** Format a nullable spend value — returns "N/A" when data is unavailable (null). */
export function formatNullableSpend(v: number | null, decimals = 2): string {
  return v === null ? "N/A" : formatPercent(v, decimals)
}

/** Truncate a set name to at most 40 Unicode code points for display in dialogs. */
export function truncateSetName(name: string, max = 40): string {
  const codePoints = Array.from(name)
  return codePoints.length > max ? `${codePoints.slice(0, max).join("")}…` : name
}

/**
 * BQ stores brand identifiers as snake_case slugs (e.g. `first_aid_beauty`).
 * Render them as title-cased human names (`First Aid Beauty`) for UI surfaces.
 * No-op for already-spaced names, preserves existing capitalization for tokens
 * that contain letters other than the leading char (e.g. `iPhone` stays `iPhone`).
 */
/**
 * adPlatforms is stored as JSON in the DB and may arrive as a string, string[], or null
 * depending on the serialisation path. Normalise to a guaranteed string[].
 */
export function normalizeAdPlatforms(raw: string[] | string | null | undefined): string[] {
  if (!raw) return []
  return Array.isArray(raw) ? (raw as string[]) : [raw as string]
}

/** Display-only map. Stored/API values stay `Creative Engine 90d` / `Creative Engine 7d`. */
export function formatOpportunityEngineLabel(
  engine: string,
  t: (key: string, defaultValue: string) => string,
): string {
  const lower = engine.toLowerCase()
  if (lower.includes("90d")) {
    return t("opportunities.engine.features", "Features engine")
  }
  if (lower.includes("7d")) {
    return t("opportunities.engine.trend", "Trend engine")
  }
  const stripped = engine.replace(/\s+Engine\b/i, "").trim()
  return stripped || engine
}

export function humanizeBrandName(raw: string): string {
  if (!raw) return raw
  return raw
    .split(/[_-]+/)
    .filter(Boolean)
    .map((token) => (token.length === 0 ? token : token[0]!.toUpperCase() + token.slice(1)))
    .join(" ")
}
